import { Link } from "react-router-dom";
import { ArrowRight, CreditCard, MousePointerClick, KeyRound } from "lucide-react";
import Reveal from "./Reveal";

const STEPS = [
    {
        icon: MousePointerClick,
        num: "1",
        title: "Choose ITF",
        body: "One plan, one price: $275 for a full year. No tiers, no add-ons, no upsells — everything is included.",
    },
    {
        icon: CreditCard,
        num: "2",
        title: "Complete payment",
        body: "Secure checkout in under a minute. You will enter your public TradingView username — never your password.",
    },
    {
        icon: KeyRound,
        num: "3",
        title: "Get access on TradingView",
        body: "We verify your order and activate ITF on your TradingView account — usually within a few hours. Find it under Indicators → Invite-only scripts.",
    },
];

export default function Steps() {
    return (
        <section id="steps" className="relative py-20 sm:py-28 lg:py-32" data-testid="chapter-steps">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Reveal>
                    <div className="mx-auto max-w-2xl text-center">
                        <p className="font-mono text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700">03 — Effortless access</p>
                        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                            On your chart in three steps.
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                            No software to install, no settings to configure, no waiting days. From payment to your first
                            signal, the whole process is measured in minutes.
                        </p>
                    </div>
                </Reveal>

                <div className="relative mt-16 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
                    <div className="absolute left-[16%] right-[16%] top-14 hidden border-t-2 border-dashed border-slate-200 md:block" />
                    {STEPS.map((s, i) => (
                        <Reveal key={s.num} delay={i * 0.14}>
                            <div className="relative h-full rounded-2xl border border-slate-200/80 bg-white p-7 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                                <div className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-emerald-400 shadow-lg">
                                    <s.icon className="h-6 w-6" />
                                    <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 font-mono text-[11px] font-bold text-white">
                                        {s.num}
                                    </span>
                                </div>
                                <h3 className="mt-6 font-display text-lg font-bold tracking-tight text-slate-900">{s.title}</h3>
                                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{s.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.2}>
                    <div className="mt-12 text-center">
                        <Link
                            to="/checkout"
                            data-testid="steps-cta-button"
                            className="group inline-flex items-center gap-2 rounded-full bg-emerald-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-emerald-600/20 transition-all duration-200 hover:scale-[1.03] hover:bg-emerald-700 active:scale-[0.98]"
                        >
                            Start step one now
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
                            We never ask for your TradingView password
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
